import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import ModalTemplate from '../../../../uiKit/ModalTemplate'
import Input from '../../../../uiKit/inputs/Input'
import Tooltip from '../../../../uiKit/TooltipInfo/Tooltip'
import classes from './styles.module.scss'

const SetupEmailAutomationModal = props => {
  const { open, onClose, onSave, mode, initialSenderEmail, initialRedirectionEmail, activeBotId } = props
  const [receiverEmail] = useState('caredata@carebrain.ai')
  const [senderEmail, setSenderEmail] = useState('')
  const [redirectionEmail, setRedirectionEmail] = useState('')
  const [errors, setErrors] = useState({ senderEmail: '', redirectionEmail: '' })

  useEffect(() => {
    if (!open) return

    if (mode === 'edit') {
      setSenderEmail(initialSenderEmail || '')
      setRedirectionEmail(initialRedirectionEmail || '')
    } else {
      setSenderEmail('')
      setRedirectionEmail('')
    }

    setErrors({ senderEmail: '', redirectionEmail: '' })
  }, [open, mode, initialSenderEmail, initialRedirectionEmail])

  const isValidEmail = value => {
    if (!value) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())
  }

  const isPrimaryButtonDisabled = useMemo(() => {
    return (
      !senderEmail ||
      !redirectionEmail ||
      !isValidEmail(receiverEmail) ||
      !isValidEmail(senderEmail) ||
      !isValidEmail(redirectionEmail) ||
      Boolean(errors.senderEmail || errors.redirectionEmail)
    )
  }, [errors, receiverEmail, redirectionEmail, senderEmail])

  const validate = () => {
    const nextErrors = { senderEmail: '', redirectionEmail: '' }

    if (!senderEmail) {
      nextErrors.senderEmail = 'Sender email ID can’t be empty'
    } else if (!isValidEmail(senderEmail)) {
      nextErrors.senderEmail = 'Enter a valid sender email ID'
    }

    if (!redirectionEmail) {
      nextErrors.redirectionEmail = 'Redirection email ID can’t be empty'
    } else if (!isValidEmail(redirectionEmail)) {
      nextErrors.redirectionEmail = 'Enter a valid redirection email ID'
    }

    setErrors(nextErrors)

    return !nextErrors.senderEmail && !nextErrors.redirectionEmail
  }

  const handleSave = () => {
    const isValid = validate()
    if (!isValid) return

    onSave({ senderEmail, redirectionEmail, receiverEmail })
    setSenderEmail('')
    setRedirectionEmail('')
    setErrors({ senderEmail: '', redirectionEmail: '' })
  }

  const handleClose = () => {
    setSenderEmail('')
    setRedirectionEmail('')
    setErrors({ senderEmail: '', redirectionEmail: '' })
    onClose()
  }

  return (
    <ModalTemplate
      title={mode === 'edit' ? 'Edit Email Automation' : 'Set up Email Automation'}
      titleClassName={classes.modalTitle}
      open={open}
      onClose={handleClose}
      onSave={handleSave}
      primaryButtonTitle={mode === 'edit' ? 'Update' : 'Save'}
      isPrimaryButtonDisabled={isPrimaryButtonDisabled}>
      <div className={classes.body}>
        <div className={classes.sectionTitle}>Email Configuration</div>
        <div className={classes.sectionDescription}>{mode === 'edit' ? 'Update the email IDs used for automated email delivery.' : 'Add the required email IDs to enable automated email delivery.'}</div>

        <div className={classes.labelRow}>
          <div className={classes.label}>
            Sender email ID <span className={classes.required}>*</span>
          </div>
          <Tooltip
            tooltipId="sender-email-tooltip"
            tooltipText="Email ID from which care plans are received"
            classes={{ tooltip: classes.customTooltip }}
          />
        </div>
        <div className={classes.inputWrap}>
          <Input
            title={null}
            placeholder="e.g. noreply@companyname.com"
            value={senderEmail}
            onChange={e => {
              setSenderEmail(e.target.value)
              if (errors.senderEmail) setErrors(prev => ({ ...prev, senderEmail: '' }))
            }}
            error={Boolean(errors.senderEmail)}
            errorMessage={errors.senderEmail}
            inputTitleStyles={{ margin: 0 }}
          />
        </div>

        <div className={classes.labelRowSpaced}>
          <div className={classes.label}>
            Redirection email ID <span className={classes.required}>*</span>
          </div>
          <Tooltip
            tooltipId="redirection-email-tooltip"
            tooltipText="Email ID which will receive alerts whenever a care plan document fails to process"
            classes={{ tooltip: classes.customTooltip }}
          />
        </div>
        <div className={classes.inputWrap}>
          <Input
            title={null}
            placeholder="Enter redirection email ID"
            value={redirectionEmail}
            onChange={e => {
              setRedirectionEmail(e.target.value)
              if (errors.redirectionEmail) setErrors(prev => ({ ...prev, redirectionEmail: '' }))
            }}
            error={Boolean(errors.redirectionEmail)}
            errorMessage={errors.redirectionEmail}
            inputTitleStyles={{ margin: 0 }}
          />
        </div>
        <div className={classes.labelRowSpaced}>
          <div className={classes.label}>CareBrain email ID</div>
        </div>
        <div className={`${classes.inputWrap} ${classes.receiverInput}`}>
          <Input
            title={null}
            placeholder="Receiver’s email ID"
            value={receiverEmail || ''}
            inputTitleStyles={{ margin: 0 }}
            disabled
          />
        </div>
      </div>
    </ModalTemplate>
  )
}

SetupEmailAutomationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['setup', 'edit']),
  initialSenderEmail: PropTypes.string,
  initialRedirectionEmail: PropTypes.string,
}

export default SetupEmailAutomationModal
